<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <div data-app>
    <topic-context-site-header
      :message-search-url="headerInfo.messageSearchUrl"
      :site-name="headerInfo.siteName"
      :site-id="headerInfo.siteId"
      :forum-id="headerInfo.forumId"
      :forum="decodeURIComponent(headerInfo.forum)"
      :has-forum-notes="headerInfo.hasForumNotes"
      :in-watchlist="topicInWatchlist"
      :site-url="siteUrl"
    />

    <pagination-component
      itemName="Topic"
      :loading="loading"
      :totalItems="totalTopics"
      :initialParams="initialParams"
      :sortOptions="sortOptions"
      :showNavOptions="true"
      :syncPaginationControls="performPaginationSync"
      @syncPaginationControls="syncPagination"
      @performSearch="fetchTopicIndices"
    />

    <div
      v-if="loading"
      class="alert alert--info u-text-centre"
    >
      {{ gettext('Loading...') }}
    </div>

    <div
      v-if="!loading && totalTopics > 0"
      class="site-index"
    >
      <notes-legend />
      <ul id="search-results">
        <li
          v-for="(topic, index) in topicsData"
          :key="index"
          class="site-index__row"
        >
          <p class="site-index__name">
            <a
              v-if="topic.topic || topic.topic_id"
              :href="topicLink(topic)"
              class="link--search"
            >
              <span v-if="topic.topic">{{ topic.topic }}</span>
              <i v-else>(no topic name)</i>
            </a>
            <i v-else>(no topic name)</i>
            <note-buttons
              :noteId="topic.topic_id"
              :noteName="topic.topic"
              :noteLinked="topic.uuid"
              :noteSiteId="topic.site_id"
              :noteSiteName="topic.site_name"
              :noteItem="'Topic'"
              :hasNotes="topic.has_topic_notes"
              :indexType="'forum'"
              :siteLink="getTopicLink(topic.site_name, topic.topic, topic.topic_id)"
              :watchlistMessage="{
                'topic_watchlist': topic.topic_watchlist,
                'url':topic.url,
                '_source': {
                  'site_id': topic.site_id,
                  'site_name': topic.site_name,
                  'topic': topic.topic,
                  'site':topic.site
                }
              }"
              @refresh="fetchTopicIndices"
            />
          </p>
          <div class="site-index__info">
            <i class="u-fontsize-sm">
              Last post in topic:
            </i>
            <br>
            <span v-if="topic.username">
              by
              <a
                :href="'/en/message-search/?filters=site_name:' + topic.site_name + ',username:' + topic.username"
                class="link--search"
              >
                {{ topic.username }}
              </a>
              <note-buttons
                :noteId="topic.user_id"
                :noteName="topic.username"
                :noteLinked="topic.uuid"
                :noteSiteId="topic.site_id"
                :noteSiteName="topic.site_name"
                :noteItem="'user'"
                :hasNotes="topic.has_user_notes"
                :indexType="'forum'"
                :siteLink="getUserLink(topic.site_name, topic.username)"
                :watchlistMessage="{
                  'username': topic.username,
                  'watchlist': topic.watchlist,
                  'url':topic.url,
                  'site': {
                    'site_id': topic.site_id,
                    'site_name': topic.site_name,
                    'site': topic.site
                  }
                }"
                @refresh="fetchTopicIndices"
              />
            </span>
            <a
              class="u-pl-sm btn--sm"
              :href="`/en/user/detail/${topic.username}/${topic.site_name}`"
            >
              View Username Details
              <svg
                class="icon u-fill-current u-fontsize-sm"
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 100 100"
              >
                <path d="M47 2L35 14l27 27-40-1H6v20h16l40-1-27 27 12 12 48-48z" />
              </svg>
            </a>
            <br>
            {{ formatDate(topic.posted_date) }}
          </div>
        </li>
      </ul>
    </div>

    <div
      v-if="!loading && totalTopics === 0"
    >
      <p class="alert alert--error">
        No topics found
      </p>
    </div>

    <pagination-component
      itemName="Topic"
      :loading="loading"
      :isPrimary="false"
      :totalItems="totalTopics"
      :syncPaginationControls="performPaginationSync"
      @syncPaginationControls="syncPagination"
      @performSearch="fetchTopicIndices"
    />
  </div>
</template>

<script>
import NoteButtons from './notes/NoteButtons'
import NotesLegend from './notes/NotesLegend'
import PaginationComponent from './PaginationComponent'
import TopicContextSiteHeader from './forum_search/TopicContextSiteHeader.vue'
import axios from 'axios'
import Cookies from 'js-cookie'

export default {
  name: 'TopicsIndex',
  components: {
    TopicContextSiteHeader,
    NoteButtons,
    NotesLegend,
    PaginationComponent,
  },
  props: {
    siteName: {
      type: String,
      required: true
    },
    forum: {
      type: String,
      required: true
    },
    headerInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      topicsData: [],
      loading: false,
      topicInWatchlist: false,
      siteUrl: '',
      totalTopics: 0,
      initialParams: {
        start: 0,
        limit: 25,
        sort: '-posted_date'
      },
      sortOptions: {
        '-posted_date': 'Most Recent',
        '+topic': 'Alphabetical',
      },
      performPaginationSync: false,
    }
  },
  computed: {
    topicsIndexURL() {
      return `/en/indices/${this.siteName}/${this.forum}/topic_index/`
    }
  },
  mounted() {
    // data retrieval request is emitted by pagination component
  },
  methods: {
    syncPagination(performSync) {
      this.$nextTick(() => {
        this.performPaginationSync = performSync
      })
    },
    getTopicLink(siteName, topic, topicId) {
      let getSiteName = (siteName) ? 'site_name:' + siteName : ''
      let getTopicName = (topic) ? ',topic:' + this.encodeURL(topic) : ''
      let getTopicId = (topicId) ? ',topic_id:' + topicId : ''
      return new URL('/en/message-search/?filters=' + getSiteName + getTopicName + getTopicId, document.baseURI).href
    },
    getUserLink(siteName, userName) {
      return new URL('/en/message-search/?filters=site_name:' + siteName + ',username:' + userName, document.baseURI).href
    },
    encodeURL(topic) {
      return encodeURIComponent(topic)
    },
    topicLink(topic) {
      let siteName = (topic.site_name) ? 'site_name:' + topic.site_name : ''
      let topicId = (topic.topic_id) ? ',topic_id:' + topic.topic_id : ''
      let topicName = (!topic.topic_id && topic.topic) ? ',topic: ' + this.encodeURL(topic.topic) : ''
      return '/en/message-search/?filters=' + siteName + topicId + topicName
    },
    formatDate(date) {
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }
      return new Date(date * 1000).toLocaleString('en-US', options)
    },
    fetchTopicIndices() {
      this.loading = true

      let searchParams = new URLSearchParams(window.location.search)
      // unfortunately we need to convert search params object into a regular js
      // object so it may be passed into request
      let requestParams = {}
      for (const [key, value] of searchParams) {
        requestParams[key] = value
      }

      const csrftoken = Cookies.get('csrftoken')
      axios.get(this.topicsIndexURL, {headers: {'X-CSRFToken': csrftoken}, params: requestParams})
        .then(response => {
          this.topicsData = response.data.data
          this.totalTopics = response.data.total
          this.siteUrl = this.topicsData[0].url
          this.topicInWatchlist = this.topicsData[0].forum_watchlist
        })
        .catch(error => {
          console.log(error)
          this.topicsData = []
          this.totalTopics = 0
        }).finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.v-progress-circular,
.loading-text {
  margin: auto;
}
</style>
